import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { TextB, TextItalic, Link as LinkIcon, ListBullets, ListNumbers, TextUnderline as UnderlineIcon } from '@phosphor-icons/react';
import { useEffect } from 'react';

interface Props {
  value: string;
  onChange: (html: string) => void;
  autoFocus?: boolean;
}

export function RichEditor({ value, onChange, autoFocus = false }: Props) {
  const editor = useEditor({
    extensions: [StarterKit, Link.configure({ openOnClick: false })],
    content: value,
    autofocus: autoFocus ? 'end' : false,
    onUpdate: ({ editor }) => onChange(editor.getHTML())
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  if (!editor) return null;

  const Btn = ({ icon: Icon, active, onClick, label }: { icon: any; active?: boolean; onClick: () => void; label: string }) => (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={`p-1.5 rounded hover:bg-stone-100 ${active ? 'bg-stone-200' : ''}`}
    >
      <Icon size={14} weight="bold" />
    </button>
  );

  return (
    <div className="border border-brand-green-100 rounded-md bg-white overflow-hidden">
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-stone-200">
        <select
          value={editor.isActive('heading', { level: 1 }) ? 'h1' : editor.isActive('heading', { level: 2 }) ? 'h2' : 'p'}
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'p') editor.chain().focus().setParagraph().run();
            else editor.chain().focus().toggleHeading({ level: v === 'h1' ? 1 : 2 }).run();
          }}
          className="text-xs px-2 py-1 border border-stone-300 rounded"
        >
          <option value="p">Format</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
        </select>
        <span className="w-px h-5 bg-stone-300 mx-1" />
        <Btn icon={TextB} active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} label="Bold" />
        <Btn icon={TextItalic} active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} label="Italic" />
        <span className="w-px h-5 bg-stone-300 mx-1" />
        <Btn
          icon={LinkIcon}
          active={editor.isActive('link')}
          onClick={() => {
            const url = window.prompt('URL del enlace');
            if (url) editor.chain().focus().setLink({ href: url }).run();
            else editor.chain().focus().unsetLink().run();
          }}
          label="Link"
        />
        <span className="w-px h-5 bg-stone-300 mx-1" />
        <Btn icon={ListBullets} active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} label="Bullet" />
        <Btn icon={ListNumbers} active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} label="Ordered" />
      </div>
      <EditorContent editor={editor} className="text-sm min-h-[200px]" />
    </div>
  );
}
